import { useQuery } from "@apollo/client";
import { useEffect } from "react";
import {
  ACTIVE_CALL_TRANSCRIPT_STATUS_QUERY,
  type ActiveCallTranscriptStatusResponse,
  type TranscriptSummaryStatus
} from "./queries";
import { shouldPollActiveCallTranscript } from "./transcriptPollUtils";

export { isTranscriptSummaryTerminal, shouldPollActiveCallTranscript } from "./transcriptPollUtils";

export const ACTIVE_CALL_TRANSCRIPT_POLL_INTERVAL_MS = 5000;

export type UseActiveCallTranscriptPollOptions = {
  enabled: boolean;
  callEnded: boolean;
  callSid?: string | null;
};

export type UseActiveCallTranscriptPollResult = {
  callSid: string | null;
  endedAt: string | null;
  transcriptSummaryStatus: TranscriptSummaryStatus | null;
  transcriptSummary: string | null;
  interactionId: string | null;
  loading: boolean;
  isPolling: boolean;
};

export default function useActiveCallTranscriptPoll({
  enabled,
  callEnded,
  callSid
}: UseActiveCallTranscriptPollOptions): UseActiveCallTranscriptPollResult {
  const shouldQuery = enabled && callEnded && !!callSid;

  const { data, loading, startPolling, stopPolling } = useQuery<ActiveCallTranscriptStatusResponse>(
    ACTIVE_CALL_TRANSCRIPT_STATUS_QUERY,
    {
      variables: { callSid: callSid || "" },
      skip: !shouldQuery,
      fetchPolicy: "network-only"
    }
  );

  const response = data?.twilioFlexCall;
  const responseMatchesSelection = !!callSid && response?.callSid === callSid;
  const transcriptSummaryStatus = responseMatchesSelection ? response.transcriptSummaryStatus : null;
  const isPolling = shouldPollActiveCallTranscript({
    enabled,
    callEnded,
    callSid,
    transcriptSummaryStatus
  });

  useEffect(() => {
    if (!shouldQuery) {
      stopPolling();
      return;
    }

    if (isPolling) {
      startPolling(ACTIVE_CALL_TRANSCRIPT_POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [isPolling, shouldQuery, startPolling, stopPolling]);

  return {
    callSid: responseMatchesSelection ? response.callSid : null,
    endedAt: responseMatchesSelection ? response.endedAt : null,
    transcriptSummaryStatus,
    transcriptSummary: responseMatchesSelection ? response.transcriptSummary : null,
    interactionId: responseMatchesSelection ? response.interactionId : null,
    loading: shouldQuery && loading,
    isPolling
  };
}
